import AppConsts from '../lib/appconst';

declare var abp: any;

export interface AbpTokenSource {
  accessToken?: string;
  idToken?: string;
}

/** First `=` only — JWT padding must not drop Abp.AuthToken. */
export function readCookiePair(cookieHeader: string, key: string): string | null {
  if (!cookieHeader || !key) {
    return null;
  }
  const parts = cookieHeader.split('; ');
  for (const part of parts) {
    if (!part) {
      continue;
    }
    const separator = part.indexOf('=');
    if (separator < 0) {
      continue;
    }
    if (decodeURIComponent(part.slice(0, separator)) === key) {
      return decodeURIComponent(part.slice(separator + 1) || '');
    }
  }
  return null;
}

export function jwtPayload(token?: string): { exp?: number; aud?: string | string[] } | null {
  if (!token || token.split('.').length < 2) {
    return null;
  }
  try {
    const payload = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = payload + '='.repeat((4 - (payload.length % 4)) % 4);
    return JSON.parse(atob(padded));
  } catch {
    return null;
  }
}

export function jwtAudience(token?: string): string[] {
  const aud = jwtPayload(token)?.aud;
  if (aud == null) {
    return [];
  }
  return (Array.isArray(aud) ? aud : [aud]).map((value) => String(value));
}

/**
 * Host JwtBearer ValidAudience is the B2C ClientId (id_token aud).
 * Prefer the access token when it is issued for that same audience (ABP API).
 */
export function pickAbpApiToken(response: AbpTokenSource, clientId?: string): string {
  const access = (response.accessToken || '').trim();
  const id = (response.idToken || '').trim();
  const audience = (clientId || AppConsts.clientId || '').trim();
  if (access && (!audience || jwtAudience(access).includes(audience))) {
    return access;
  }
  return id || access;
}

/** JWT `exp` is unix seconds. Zero TokenAuth uses duration seconds. */
export function cookieExpireFromJwt(token?: string, expClaim?: number): Date {
  const fromToken = jwtPayload(token)?.exp;
  const exp = fromToken || expClaim;
  if (exp && exp > 1e9) {
    return new Date(exp * 1000);
  }
  if (exp && exp < 1e7) {
    return new Date(Date.now() + exp * 1000);
  }
  return new Date(Date.now() + 60 * 60 * 1000);
}

/** ABP JS contract: Abp.AuthToken + enc_auth_token. Host also reads the cookie. */
export function persistAbpAuthCookies(token: string, expireDate: Date): void {
  if (!token) {
    throw new Error('Missing ABP auth token');
  }
  abp.auth.setToken(token, expireDate);
  abp.utils.setCookieValue(
    AppConsts.authorization.encrptedAuthTokenName,
    token,
    expireDate,
    abp.appPath
  );
}

export function applyGrantedPoliciesToAbp(policies?: Record<string, boolean> | null): void {
  if (!policies || !abp?.auth) {
    return;
  }
  abp.auth.allPermissions = abp.auth.allPermissions || {};
  abp.auth.grantedPermissions = abp.auth.grantedPermissions || {};
  Object.keys(policies).forEach((permission) => {
    if (policies[permission]) {
      abp.auth.allPermissions[permission] = true;
      abp.auth.grantedPermissions[permission] = true;
    }
  });
}

export function isAnonymousZeroConfig(result: any): boolean {
  const grants = result?.auth?.grantedPermissions;
  const grantCount = grants && typeof grants === 'object' ? Object.keys(grants).length : 0;
  if (grantCount > 0) {
    return false;
  }
  const userId = result?.session?.userId;
  return userId == null || userId === 0 || userId === '0';
}
